const BYYSC_VERTEX_SHADER = `
attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const BYYSC_FRAGMENT_SHADER = `
precision highp float;

uniform vec2 u_resolution;
uniform float u_time;
uniform vec3 u_color0;
uniform vec3 u_color1;
uniform vec3 u_color2;
uniform vec3 u_color3;
uniform vec3 u_color4;
uniform float u_contour;
uniform float u_rotation;
uniform float u_noise;
uniform float u_innerGlow;
uniform float u_outerGlow;
uniform float u_scale;
uniform float u_speed;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + vec2(13.7, 7.1);
    a *= 0.5;
  }
  return v;
}

mat2 rot2d(float angle) {
  float c = cos(angle);
  float s = sin(angle);
  return mat2(c, -s, s, c);
}

vec3 paletteSample(float f) {
  f = clamp(f, 0.0, 1.0);
  float seg = f * 4.0;
  if (seg < 1.0) return mix(u_color0, u_color1, seg);
  if (seg < 2.0) return mix(u_color1, u_color2, seg - 1.0);
  if (seg < 3.0) return mix(u_color2, u_color3, seg - 2.0);
  return mix(u_color3, u_color4, seg - 3.0);
}

void main() {
  vec2 res = u_resolution;
  vec2 centered = (gl_FragCoord.xy - 0.5 * res) / max(res.x, res.y);
  vec2 uv = centered;

  uv *= u_scale;
  uv = rot2d(radians(u_rotation)) * uv;

  float t = u_time * 0.001 * (u_speed + 0.05);

  vec2 q = vec2(
    fbm(uv + vec2(0.0, t)),
    fbm(uv + vec2(5.2, 1.3) + vec2(t * 0.8, t * 0.4))
  );
  vec2 r = vec2(
    fbm(uv + 3.5 * q + vec2(1.7, 9.2) + vec2(-t * 0.6, t * 0.2)),
    fbm(uv + 3.5 * q + vec2(8.3, 2.8) + vec2(t * 0.3, t * 0.9))
  );
  float field = fbm(uv + 3.5 * r);

  vec3 color = paletteSample(field);

  if (u_contour > 0.001) {
    float bands = sin(field * 28.0);
    float lines = smoothstep(0.88, 1.0, bands * bands);
    color = mix(color, color * (1.0 - 0.45 * lines), u_contour);
  }

  if (u_noise > 0.001) {
    float n = hash(gl_FragCoord.xy + vec2(floor(u_time * 0.06)));
    color += (n - 0.5) * u_noise * 0.22;
  }

  float radius = length(centered);
  float inner = (1.0 - smoothstep(0.0, 0.6, radius)) * u_innerGlow;
  color += inner * 0.28;

  float outer = smoothstep(0.45, 1.15, radius) * u_outerGlow;
  color = mix(color, color * 0.6, outer);

  color = clamp(color, 0.0, 1.0);
  gl_FragColor = vec4(color, 1.0);
}
`;

class ByyscMeshShader extends HTMLElement {
  static get observedAttributes() {
    return [
      'data-colors',
      'data-contour',
      'data-rotation',
      'data-noise',
      'data-inner-glow',
      'data-outer-glow',
      'data-speed',
      'data-scale',
    ];
  }

  constructor() {
    super();
    this._rafId = null;
    this._startTime = 0;
    this._gl = null;
    this._program = null;
    this._uniforms = {};
    this._settings = null;
    this._resizeObserver = null;
    this._dpr = 1;
    this._reducedMotion = false;
    this._lastFrameTime = 0;
    this._render = this._render.bind(this);
  }

  connectedCallback() {
    this._reducedMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this._canvas = document.createElement('canvas');
    this._canvas.className = 'byysc-mesh-shader__canvas';
    this._canvas.setAttribute('aria-hidden', 'true');
    this.appendChild(this._canvas);

    const gl =
      this._canvas.getContext('webgl', {
        antialias: false,
        premultipliedAlpha: false,
        preserveDrawingBuffer: false,
        powerPreference: 'low-power',
      }) ||
      this._canvas.getContext('experimental-webgl');

    if (!gl) {
      this.classList.add('byysc-mesh-shader--unsupported');
      return;
    }

    this._gl = gl;
    this._readSettings();

    if (!this._initShader()) {
      this.classList.add('byysc-mesh-shader--unsupported');
      return;
    }

    this._initGeometry();
    this._resize();

    this._resizeObserver = new ResizeObserver(() => this._resize());
    this._resizeObserver.observe(this);

    this._startTime = performance.now();
    this.classList.add('byysc-mesh-shader--ready');
    this._rafId = requestAnimationFrame(this._render);
  }

  disconnectedCallback() {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this._gl && this._program) {
      const loseExt = this._gl.getExtension('WEBGL_lose_context');
      if (loseExt) loseExt.loseContext();
    }
    this._gl = null;
    this._program = null;
  }

  attributeChangedCallback() {
    if (this._gl) this._readSettings();
  }

  _readSettings() {
    const ds = this.dataset;
    const rawColors = (
      ds.colors || '#FFFFFF,#D915EF,#F8B3FF,#FFFFFF,#FFFFFF'
    ).split(',');
    const colors = [];
    for (let i = 0; i < 5; i++) {
      colors.push(this._hexToRgb(rawColors[i] || '#FFFFFF'));
    }
    this._settings = {
      colors,
      contour: this._parseFloat(ds.contour, 0.3),
      rotation: this._parseFloat(ds.rotation, 0),
      noise: this._parseFloat(ds.noise, 0.2),
      innerGlow: this._parseFloat(ds.innerGlow, 0.5),
      outerGlow: this._parseFloat(ds.outerGlow, 0.3),
      speed: this._parseFloat(ds.speed, 0.6),
      scale: this._parseFloat(ds.scale, 1.65),
    };
  }

  _parseFloat(value, fallback) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : fallback;
  }

  _hexToRgb(hex) {
    const cleaned = String(hex || '#FFFFFF').trim().replace('#', '');
    const full =
      cleaned.length === 3
        ? cleaned
            .split('')
            .map((c) => c + c)
            .join('')
        : cleaned.padEnd(6, 'f').slice(0, 6);
    const num = parseInt(full, 16);
    if (!Number.isFinite(num)) return [1, 1, 1];
    return [
      ((num >> 16) & 255) / 255,
      ((num >> 8) & 255) / 255,
      (num & 255) / 255,
    ];
  }

  _compile(type, source) {
    const gl = this._gl;
    const sh = gl.createShader(type);
    gl.shaderSource(sh, source);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      console.error('[byysc-mesh-shader] shader compile failed:', gl.getShaderInfoLog(sh));
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  _initShader() {
    const gl = this._gl;
    const vs = this._compile(gl.VERTEX_SHADER, BYYSC_VERTEX_SHADER);
    const fs = this._compile(gl.FRAGMENT_SHADER, BYYSC_FRAGMENT_SHADER);
    if (!vs || !fs) return false;
    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('[byysc-mesh-shader] link failed:', gl.getProgramInfoLog(program));
      return false;
    }
    this._program = program;
    gl.useProgram(program);

    this._uniforms = {
      resolution: gl.getUniformLocation(program, 'u_resolution'),
      time: gl.getUniformLocation(program, 'u_time'),
      color0: gl.getUniformLocation(program, 'u_color0'),
      color1: gl.getUniformLocation(program, 'u_color1'),
      color2: gl.getUniformLocation(program, 'u_color2'),
      color3: gl.getUniformLocation(program, 'u_color3'),
      color4: gl.getUniformLocation(program, 'u_color4'),
      contour: gl.getUniformLocation(program, 'u_contour'),
      rotation: gl.getUniformLocation(program, 'u_rotation'),
      noise: gl.getUniformLocation(program, 'u_noise'),
      innerGlow: gl.getUniformLocation(program, 'u_innerGlow'),
      outerGlow: gl.getUniformLocation(program, 'u_outerGlow'),
      scale: gl.getUniformLocation(program, 'u_scale'),
      speed: gl.getUniformLocation(program, 'u_speed'),
    };
    return true;
  }

  _initGeometry() {
    const gl = this._gl;
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW
    );
    const loc = gl.getAttribLocation(this._program, 'a_position');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  }

  _resize() {
    if (!this._gl || !this._canvas) return;
    this._dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = this.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width * this._dpr));
    const h = Math.max(1, Math.round(rect.height * this._dpr));
    if (this._canvas.width !== w || this._canvas.height !== h) {
      this._canvas.width = w;
      this._canvas.height = h;
      this._gl.viewport(0, 0, w, h);
    }
  }

  _render(now) {
    if (!this._gl || !this._program) return;
    const gl = this._gl;
    const s = this._settings;

    if (this._canvas.width <= 1 || this._canvas.height <= 1) {
      this._resize();
    }

    let elapsed = now - this._startTime;
    if (this._reducedMotion) elapsed *= 0.3;

    gl.uniform2f(this._uniforms.resolution, this._canvas.width, this._canvas.height);
    gl.uniform1f(this._uniforms.time, elapsed);
    gl.uniform3fv(this._uniforms.color0, s.colors[0]);
    gl.uniform3fv(this._uniforms.color1, s.colors[1]);
    gl.uniform3fv(this._uniforms.color2, s.colors[2]);
    gl.uniform3fv(this._uniforms.color3, s.colors[3]);
    gl.uniform3fv(this._uniforms.color4, s.colors[4]);
    gl.uniform1f(this._uniforms.contour, s.contour);
    gl.uniform1f(this._uniforms.rotation, s.rotation);
    gl.uniform1f(this._uniforms.noise, s.noise);
    gl.uniform1f(this._uniforms.innerGlow, s.innerGlow);
    gl.uniform1f(this._uniforms.outerGlow, s.outerGlow);
    gl.uniform1f(this._uniforms.scale, s.scale);
    gl.uniform1f(this._uniforms.speed, s.speed);

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    this._rafId = requestAnimationFrame(this._render);
  }
}

if (!customElements.get('byysc-mesh-shader')) {
  customElements.define('byysc-mesh-shader', ByyscMeshShader);
}
